/**
 * InstallPrompt — a calm, dismissible bottom sheet inviting Home-Screen
 * install. Shown contextually (after a value moment / return visit), never on
 * first load. Android gets a one-tap button; iOS Safari gets the Share →
 * "Add to Home Screen" instruction (no programmatic prompt on iOS).
 */
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

export function InstallPrompt({
  isIos,
  canPrompt,
  onInstall,
  onDismiss,
}: {
  isIos: boolean;
  canPrompt: boolean;
  onInstall: () => void;
  onDismiss: () => void;
}) {
  const { t, i18n } = useTranslation();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onDismiss();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onDismiss]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center font-kol"
      role="dialog"
      aria-modal="true"
      aria-label={t('kol.install.title', { defaultValue: 'התקנת קרנף' })}
      dir={i18n.dir()}
    >
      <button
        type="button"
        aria-label={t('common.close', { defaultValue: 'סגירה' })}
        onClick={onDismiss}
        className="absolute inset-0 bg-black/30"
      />
      <div className="kol-glass-sheet relative w-full max-w-md rounded-t-[30px] px-6 pt-5 pb-[max(20px,env(safe-area-inset-bottom))] shadow-kol-up animate-kol-rise">
        <div className="mx-auto mb-4 h-[5px] w-10 rounded-full bg-kol-line" aria-hidden />
        <div className="flex items-start gap-4">
          <div className="kol-orb shrink-0" data-state="idle" style={{ width: 52, height: 52 }} aria-hidden />
          <div className="flex-1">
            <h2 className="text-[20px] font-extrabold text-kol-ink leading-snug">
              {t('kol.install.title', { defaultValue: 'שמרו את הרכב במסך הבית' })}
            </h2>
            <p className="text-[15px] text-kol-ink-soft leading-relaxed mt-1.5">
              {t('kol.install.body', {
                defaultValue: 'גישה מהירה, המסמכים זמינים גם בלי רשת, ותזכורות לפני שהביטוח או הטסט נגמרים.',
              })}
            </p>
          </div>
        </div>

        {isIos && !canPrompt ? (
          <div className="mt-5 rounded-2xl bg-kol-sage-soft p-4 text-[15px] text-kol-ink leading-relaxed">
            {t('kol.install.ios_1', { defaultValue: 'הקישו על' })}{' '}
            <span className="inline-flex items-center gap-1 font-bold text-kol-sage-deep">
              {t('kol.install.ios_share', { defaultValue: 'שיתוף' })} <span aria-hidden>⬆️</span>
            </span>{' '}
            {t('kol.install.ios_2', { defaultValue: 'ואז בחרו' })}{' '}
            <span className="font-bold text-kol-sage-deep">
              {t('kol.install.ios_a2hs', { defaultValue: '« הוסף למסך הבית »' })}
            </span>
            .
            <button
              type="button"
              onClick={onDismiss}
              className="mt-4 w-full rounded-2xl bg-kol-pine text-white font-semibold py-3.5 text-[16px]"
            >
              {t('kol.install.got_it', { defaultValue: 'הבנתי' })}
            </button>
          </div>
        ) : (
          <div className="mt-5 flex flex-col gap-2.5">
            <button
              type="button"
              onClick={onInstall}
              className="w-full rounded-2xl bg-kol-pine text-white font-semibold py-3.5 text-[16px]"
            >
              {t('kol.install.add', { defaultValue: 'הוספה למסך הבית' })}
            </button>
            <button
              type="button"
              onClick={onDismiss}
              className="w-full rounded-2xl text-kol-ink-faint font-medium py-3 text-[15px]"
            >
              {t('kol.install.later', { defaultValue: 'לא עכשיו' })}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
